// Authentication state synchronization across tabs/windows
interface AuthEvent {
  type: 'login' | 'logout' | 'session-refresh' | 'session-expired';
  user?: any;
  timestamp: number;
}

class AuthBroadcaster {
  private channel: BroadcastChannel | null = null;
  private listeners: Map<string, Set<(event: AuthEvent) => void>> = new Map();

  constructor() {
    // Only initialize if BroadcastChannel is supported
    if (typeof BroadcastChannel !== 'undefined') {
      this.channel = new BroadcastChannel('domelec-auth');
      this.channel.onmessage = (event) => this.handleMessage(event);
    } else {
      // Fallback to localStorage events for older browsers
      window.addEventListener('storage', this.handleStorageEvent.bind(this));
    }
  }

  private handleMessage(event: MessageEvent) {
    const authEvent = event.data as AuthEvent;
    this.notifyListeners(authEvent);
  }

  private handleStorageEvent(event: StorageEvent) {
    if (event.key === 'domelec-auth-event' && event.newValue) {
      try {
        const authEvent = JSON.parse(event.newValue) as AuthEvent;
        // Only process recent events (within 2 seconds)
        if (Date.now() - authEvent.timestamp < 2000) {
          this.notifyListeners(authEvent);
        }
      } catch (error) {
        console.error('Failed to parse auth event:', error);
      }
    }
  }

  private notifyListeners(event: AuthEvent) {
    const listeners = this.listeners.get(event.type);
    if (listeners) {
      listeners.forEach((listener) => {
        try {
          listener(event);
        } catch (error) {
          console.error('Auth listener error:', error);
        }
      });
    }
  }

  broadcast(event: Omit<AuthEvent, 'timestamp'>) {
    const fullEvent: AuthEvent = {
      ...event,
      timestamp: Date.now(),
    };

    if (this.channel) {
      this.channel.postMessage(fullEvent);
    } else {
      // Fallback to localStorage for older browsers
      localStorage.setItem('domelec-auth-event', JSON.stringify(fullEvent));
      // Clean up after a short delay
      setTimeout(() => {
        localStorage.removeItem('domelec-auth-event');
      }, 100);
    }
  }

  subscribe(type: AuthEvent['type'], listener: (event: AuthEvent) => void) {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, new Set());
    }
    this.listeners.get(type)!.add(listener);

    // Return unsubscribe function
    return () => {
      const listeners = this.listeners.get(type);
      if (listeners) {
        listeners.delete(listener);
      }
    };
  }

  destroy() {
    if (this.channel) {
      this.channel.close();
    } else {
      window.removeEventListener('storage', this.handleStorageEvent.bind(this));
    }
    this.listeners.clear();
  }
}

// Singleton instance
export const authBroadcaster = new AuthBroadcaster();
